import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const LOGOS = {
  rrdc: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/8kaelsop_Untitled%20design.png",
  turf_house: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/nltdiuae_turf%20blog.png",
};

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    const handler = () => toast.error("Session expired. Please log in again.");
    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return toast.error("Please fill in all fields");
    setLoading(true);
    try {
      const u = await login(username, password);
      toast.success(`Welcome back, ${u.first_name}!`);
      if (u.must_change_password) {
        navigate("/change-password");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative px-4">
      <Card className="w-full max-w-md border-border" data-testid="login-card">
        <CardHeader className="items-center space-y-4 pb-2">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden bg-transparent">
              <img src={LOGOS.rrdc} alt="Rolling River" className="w-11 h-11 object-contain" />
            </div>
            <div className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden bg-transparent">
              <img src={LOGOS.turf_house} alt="Turf House" className="w-11 h-11 object-contain" />
            </div>
          </div>
          <CardTitle className="font-outfit text-2xl tracking-tight">Turf Booking System</CardTitle>
          <p className="text-sm text-muted-foreground">Sign in to manage your bookings</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Username</label>
              <Input
                data-testid="login-username"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <Input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowPw(!showPw)}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <div className="text-center">
              <button type="button" className="text-sm text-muted-foreground hover:text-primary" onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>
            <div className="text-center pt-2 border-t border-border">
              <button type="button" className="text-sm text-muted-foreground hover:text-primary" onClick={() => navigate("/inquire")} data-testid="back-to-inquiry-link">
                ← Back to Inquiry Form
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
